'use client';

// ING Lease Now on product and cart pages. The same availability endpoint serves two jobs:
// eligibility without leasingParams and an installment simulation with leasingParams.

import React from 'react';
import { useRouter } from '@/core/i18n/navigation';
import { Building2, Loader2 } from 'lucide-react';
import { BlockProps } from './types';
import { cn } from '@/core/utils/cn';
import { formatPrice } from '@/core/utils/currency';
import { getAbsoluteMediaUrl } from '@/core/utils/media';
import { ensureProductInCart } from '@/lib/cart';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { useProductState } from '../ProductContext';
import { DEMO_PRODUCT } from '../demoProduct';
import {
    useLeaseNowAvailability,
    useLeaseNowCartAvailability,
    type LeaseNowSimulationParams,
} from '@/modules/shop/components/product/useLeaseNowAvailability';

const DEMO_MONTHLY_NET = 456.12;
const ALIGN_CLASS: Record<string, string> = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };

function clamp(value: unknown, min: number, max: number, fallback: number) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
}

function clampPercent(value: unknown, fallback = 100) {
    return clamp(value, 10, 100, fallback);
}

function defaultSimulationParams(settings: Record<string, any>): LeaseNowSimulationParams {
    return {
        leasingPeriodMonths: Math.round(clamp(settings.defaultLeasingPeriodMonths, 1, 120, 36)),
        ownContributionPercent: clamp(settings.defaultOwnContributionPercent, 0, 100, 20),
        residualValuePercent: clamp(settings.defaultResidualValuePercent, 0, 100, 20),
    };
}

/** The artwork and alt text are prescribed by ING Lease Now. Size belongs to the wrapper. */
function OfficialLeaseNowButton({ onClick, widthPercent }: { onClick: () => void; widthPercent: number }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="ING Lease Now"
            className="block max-w-full transition-opacity hover:opacity-90"
            style={{ width: `${widthPercent}%` }}
        >
            <img
                alt="ING Lease Now"
                className="leasenow_button-image"
                src="https://leasenow.pl/assets/ecommerce/images/leasing_button.png"
                style={{ display: 'block', width: '100%', height: 'auto' }}
            />
        </button>
    );
}

function CalculatorControl({
    id,
    label,
    value,
    min,
    max,
    suffix,
    accentColor,
    onChange,
}: {
    id: string;
    label: string;
    value: number;
    min: number;
    max: number;
    suffix: string;
    accentColor: string;
    onChange: (value: number) => void;
}) {
    const setBoundedValue = (raw: string) => onChange(clamp(raw, min, max, min));

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
                <label htmlFor={`${id}-number`} className="text-xs font-semibold text-slate-700">{label}</label>
                <div className="flex items-center gap-1">
                    <input
                        id={`${id}-number`}
                        type="number"
                        min={min}
                        max={max}
                        step={1}
                        value={value}
                        onChange={(event) => setBoundedValue(event.target.value)}
                        className="h-8 w-20 rounded-md border border-slate-200 bg-white px-2 text-right text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-slate-300"
                    />
                    <span className="min-w-9 text-xs text-slate-500">{suffix}</span>
                </div>
            </div>
            <input
                id={`${id}-range`}
                aria-label={label}
                type="range"
                min={min}
                max={max}
                step={1}
                value={value}
                onChange={(event) => setBoundedValue(event.target.value)}
                className="h-2 w-full cursor-pointer accent-current"
                style={{ color: accentColor }}
            />
        </div>
    );
}

/** Admin canvas: tells the merchant why the block will not show on the storefront yet. */
function AdminHint({ enabled }: { enabled: boolean }) {
    return (
        <p className="mt-2 text-[10px] font-semibold text-amber-700">
            {enabled
                ? 'Podgląd — w sklepie blok pokaże się tylko wtedy, gdy produkt lub koszyk kwalifikuje się do LeaseNow.'
                : 'LeaseNow jest wyłączony w Integracjach — w sklepie blok się nie pokaże.'}
        </p>
    );
}

function LeaseNowCalculatorLoader({
    message,
    marginTop,
    marginBottom,
}: {
    message: string;
    marginTop?: string | number;
    marginBottom?: string | number;
}) {
    return (
        <div
            role="status"
            aria-live="polite"
            className="flex min-h-20 items-center gap-3 rounded-xl border border-orange-200 bg-orange-50/80 px-4 py-4 text-orange-700"
            style={{ marginTop: marginTop || undefined, marginBottom: marginBottom || undefined }}
        >
            <span className="shrink-0 rounded-lg bg-orange-100 p-2">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold">{message}</span>
        </div>
    );
}

function useLeaseNowBlockState(isAdmin: boolean, product: any, leasingParams?: LeaseNowSimulationParams) {
    const siteConfig = useSiteConfig();
    const productState = useProductState();
    const activeProduct = productState?.product || product || null;
    // "Cena na zapytanie": no price to finance — both leasing blocks stay hidden on the storefront.
    const enabled = !!siteConfig?.leasenow_enabled && !(activeProduct?.price_on_request && !isAdmin);
    const variant = productState?.selectedVariant || null;
    const quantity = activeProduct?.is_one_off ? 1 : Math.max(1, productState?.quantity || 1);
    const productLeasing = useLeaseNowAvailability(activeProduct, variant, quantity, enabled && !isAdmin && !!activeProduct, leasingParams);
    const cartLeasing = useLeaseNowCartAvailability(enabled && !isAdmin && !activeProduct, leasingParams);
    const leasing = activeProduct || isAdmin ? productLeasing : cartLeasing;
    return {
        enabled,
        context: activeProduct || isAdmin ? 'product' as const : 'cart' as const,
        activeProduct: activeProduct || (isAdmin ? DEMO_PRODUCT : null),
        variant,
        quantity,
        ...leasing,
    };
}

function useStartLeaseNow(state: ReturnType<typeof useLeaseNowBlockState>, isAdmin: boolean) {
    const router = useRouter();

    return () => {
        if (isAdmin) return;
        // Lease Now's availability response already carries the leasing application for exactly
        // this product/quantity (or cart): reservationId + redirectUrl. Open it in a new tab, like
        // the cart's Lease Now button and the product badge do — the FILLED webhook creates the
        // ShopStag order (leasenow_router.notification), so no checkout round-trip is needed.
        if (state.data?.redirectUrl) {
            window.open(state.data.redirectUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        // No redirectUrl (older API response / error): fall back to the checkout handoff.
        if (state.context === 'product' && state.activeProduct) {
            const mainImage = state.activeProduct.images?.find((img: any) => img.is_main) || state.activeProduct.images?.[0];
            ensureProductInCart(state.activeProduct, state.variant, state.quantity, mainImage ? getAbsoluteMediaUrl(mainImage.path) : null);
        }
        // The checkout creates the ShopStag order and then hands the customer over using the
        // reservation returned by the same Lease Now endpoint.
        router.push('/zamowienie?paymentMethod=leasenow');
    };
}

// ─── product-leasenow-button ────────────────────────────────────────────────

export function LeaseNowButtonBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const { locale } = useTranslation();
    const state = useLeaseNowBlockState(isAdmin, product);
    const startLeaseNow = useStartLeaseNow(state, isAdmin);
    const settings = block.settings || {};

    if (!isAdmin && (!state.enabled || !state.available || (state.context === 'product' && !state.activeProduct))) return null;

    const align = ALIGN_CLASS[settings.align || 'left'] || ALIGN_CLASS.left;
    // fullWidth is only a migration fallback for blocks saved before the official-image change.
    const legacyWidth = settings.fullWidth ? 100 : 65;
    const widthPercent = clampPercent(currentStyles?.imageWidthPercent ?? settings.imageWidthPercent, legacyWidth);
    const apiMessage = state.data?.additionalMessage?.find((entry) => entry.languageCode === locale)?.message
        || state.data?.additionalMessage?.find((entry) => entry.languageCode === 'pl')?.message
        || state.data?.additionalMessage?.[0]?.message;
    const notes = Array.from(new Set([apiMessage, settings.subText].filter((value): value is string => !!value)));

    return (
        <div style={{ marginTop: settings.blockMarginTop || undefined, marginBottom: settings.blockMarginBottom || undefined }}>
            <div className={cn('flex', align)}>
                <OfficialLeaseNowButton onClick={startLeaseNow} widthPercent={widthPercent} />
            </div>
            {notes.map((note) => (
                <p key={note} className={cn('mt-1.5 flex text-xs', align)} style={{ color: settings.subTextColor || '#64748b' }}>{note}</p>
            ))}
            {isAdmin && <AdminHint enabled={state.enabled} />}
        </div>
    );
}

// ─── product-leasenow-calculator ────────────────────────────────────────────

export function LeaseNowCalculatorBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const { t } = useTranslation();
    const settings = block.settings || {};
    const defaults = defaultSimulationParams(settings);
    const [draftParams, setDraftParams] = React.useState<LeaseNowSimulationParams>(defaults);
    const [requestParams, setRequestParams] = React.useState<LeaseNowSimulationParams>(defaults);

    React.useEffect(() => {
        const timeout = window.setTimeout(() => setRequestParams(draftParams), 450);
        return () => window.clearTimeout(timeout);
    }, [draftParams]);

    React.useEffect(() => {
        if (!isAdmin) return;
        const nextDefaults: LeaseNowSimulationParams = {
            leasingPeriodMonths: defaults.leasingPeriodMonths,
            ownContributionPercent: defaults.ownContributionPercent,
            residualValuePercent: defaults.residualValuePercent,
        };
        setDraftParams(nextDefaults);
        setRequestParams(nextDefaults);
    }, [isAdmin, defaults.leasingPeriodMonths, defaults.ownContributionPercent, defaults.residualValuePercent]);

    const state = useLeaseNowBlockState(isAdmin, product, requestParams);
    const rate = isAdmin ? DEMO_MONTHLY_NET : state.monthlyPaymentNet;
    const hasApiError = !!state.data?.statusCode && rate === null;

    if (!isAdmin && (!state.enabled || (state.context === 'product' && !state.activeProduct))) return null;
    if (!isAdmin && !state.available) {
        if (state.loading) {
            return (
                <LeaseNowCalculatorLoader
                    message={t('leasenow.checking_availability', 'Sprawdzamy dostępność kalkulatora leasingu…')}
                    marginTop={settings.blockMarginTop}
                    marginBottom={settings.blockMarginBottom}
                />
            );
        }
        return null;
    }

    const align = settings.align || 'left';
    const accentColor = settings.accentColor || '#ea580c';
    const title = settings.title || t('leasenow.calculator_title', 'Kalkulator leasingu');
    const description = settings.description || t(
        'leasenow.calculator_description',
        'Dostosuj okres, opłatę wstępną i wartość wykupu, aby obliczyć miesięczną ratę.'
    );
    const updateParam = (
        key: 'leasingPeriodMonths' | 'ownContributionPercent' | 'residualValuePercent',
        value: number,
    ) => {
        setDraftParams((current) => ({ ...current, [key]: value }));
    };

    return (
        <div
            style={{
                marginTop: settings.blockMarginTop || undefined,
                marginBottom: settings.blockMarginBottom || undefined,
                backgroundColor: settings.bgColor || '#ffffff',
                border: `${settings.borderWidth ?? 1}px solid ${settings.borderColor || '#e2e8f0'}`,
                borderRadius: settings.borderRadius ?? 10,
                padding: currentStyles?.innerPadding ?? 18,
            }}
        >
            <div className={cn('flex items-start gap-3', align === 'center' && 'justify-center text-center', align === 'right' && 'justify-end text-right')}>
                {settings.showIcon !== false && (
                    <span className="mt-0.5 shrink-0 rounded-lg p-2" style={{ backgroundColor: settings.iconBgColor || '#fff7ed', color: accentColor }}>
                        <Building2 className="h-4 w-4" />
                    </span>
                )}
                <div className="min-w-0">
                    <p style={{ color: settings.textColor || '#0f172a', fontSize: currentStyles?.fontSize ?? 16, fontWeight: 700, lineHeight: 1.35 }}>
                        {title}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed" style={{ color: settings.descriptionColor || '#475569' }}>
                        {description}
                    </p>
                </div>
            </div>

            <div className="mt-5 grid gap-5 md:grid-cols-3">
                <CalculatorControl
                    id={`${block.id}-period`}
                    label={t('leasenow.period', 'Okres leasingu')}
                    value={draftParams.leasingPeriodMonths}
                    min={1}
                    max={120}
                    suffix={t('leasenow.months_short', 'mies.')}
                    accentColor={accentColor}
                    onChange={(value) => updateParam('leasingPeriodMonths', Math.round(value))}
                />
                <CalculatorControl
                    id={`${block.id}-contribution`}
                    label={t('leasenow.own_contribution', 'Opłata wstępna')}
                    value={draftParams.ownContributionPercent}
                    min={0}
                    max={100}
                    suffix="%"
                    accentColor={accentColor}
                    onChange={(value) => updateParam('ownContributionPercent', value)}
                />
                <CalculatorControl
                    id={`${block.id}-residual`}
                    label={t('leasenow.residual_value', 'Wartość wykupu')}
                    value={draftParams.residualValuePercent}
                    min={0}
                    max={100}
                    suffix="%"
                    accentColor={accentColor}
                    onChange={(value) => updateParam('residualValuePercent', value)}
                />
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" aria-live="polite">
                <div>
                    <p className="text-xs font-medium text-slate-500">{t('leasenow.estimated_installment', 'Szacowana rata netto')}</p>
                    <p className="mt-0.5 text-xs text-slate-400">{t('leasenow.per_month', 'miesięcznie')}</p>
                </div>
                <div className="flex items-center gap-2">
                    {state.loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" aria-hidden="true" />}
                    <strong className="text-xl" style={{ color: accentColor }}>
                        {typeof rate === 'number' ? formatPrice(rate, state.data?.currencyIsoName || 'PLN') : '—'}
                    </strong>
                </div>
            </div>

            {hasApiError && (
                <p className="mt-2 text-xs text-red-600">{t('leasenow.calculation_error', 'Nie udało się przeliczyć raty dla wybranych parametrów. Zmień wartości i spróbuj ponownie.')}</p>
            )}
            {settings.subText && (
                <p className="mt-2 text-xs" style={{ color: settings.subTextColor || '#64748b' }}>{settings.subText}</p>
            )}
            {isAdmin && <AdminHint enabled={state.enabled} />}
        </div>
    );
}
